<template>
  <div class="category-avatar"
       :class="{
         'category-avatar--active': active,
         'category-avatar--loading': loading,
       }"
  >
    <div class="category-avatar__border">
      <div v-if="active" class="category-avatar__cross">
        <Icon name="cross-fat-white"/>
      </div>
      <div class="category-avatar__thumb" v-if="thumb === '' || loading"/>
      <img :src="thumb"
           :alt="name"
           loading="lazy"
           class="category-avatar__thumb"
           v-else/>
      <div class="category-avatar__name">
        <div class="category-avatar__name-container">
          <template v-if="loading">
            <LoadingRow container-height="1.5rem"
                        rounded
            />
          </template>
          <template v-else>
            {{ name }}
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import Icon from '@/components/partials/Icon.vue'
import LoadingRow from '@/components/partials/Loading/LoadingRow.vue'

type Props = {
  active?: boolean
  thumb?: string | null
  name?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  active: false,
  thumb: null,
  name: null,
})

// Thumbs can be missing, in some cases...
const loading = computed(() => props.name === null)
</script>

<style lang="scss" scoped>
@import "@/assets/css/mixins/breakpoints-up.scss";
@import "@/assets/css/mixins/styling.scss";

$border-radius-inner: calc(0.5rem * 0.65);

.category-avatar {
  $self:      &;
  position:   relative;
  border-radius: 0.5rem;
  width:      6.5rem;
  padding:    4px;
  transition: all 0.2s;

  &__border {
    border-radius: 0.5rem;
    border: 2px solid #dee0e4;
  }

  &__thumb {
    width:                  100%;
    height:                 2.75rem;
    background-color:       #dee0e4;
    background-size:        cover;
    border-top-left-radius: $border-radius-inner;
    border-top-right-radius: $border-radius-inner;

    object-fit:             cover;
  }

  &__name {
    background-color:          #ffffff;
    border-bottom-left-radius: $border-radius-inner;
    border-bottom-right-radius: $border-radius-inner;
    padding:                   0.25rem 0.5rem 0.35rem;
    font-weight:               700;
    text-align:                center;
    font-size:                 0.875rem;
  }

  &__name-container {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow:    hidden;
    max-height:  1.5rem;
  }

  &__cross {
    @include gradient-button;
    position:      absolute;
    top:           -0.4rem;
    right:         -0.4rem;
    width:         1.25rem;
    height:        1.25rem;
    border-radius: 9999px;
    display:       flex;
    align-items:   center;
    justify-content: center;

    &:deep(.b-icon-icon) {
      width: 0.5rem;
      height: 0.5rem;
    }
  }

  &--active {
    @include gradient-button;

    #{$self}__border {
      border-color: transparent;
    }
  }
}
</style>
